import { YarnBall } from "@/components/yarn/YarnBall";
import { yarnColor } from "@/lib/api";

export const FURS = ["#E8894A", "#8E8B99", "#3B3335", "#F2D7B0", "#C85A32", "#D9A441", "#7A9E7E", "#A98BD1", "#6F93C9", "#D98AA8", "#B07A55", "#E6E1DA"];
const PATTERN_KEYS = ["plain", "stripes", "patch", "socks"];
export const PATTERNS = [
  { value: "plain", label: "Plain" },
  { value: "stripes", label: "Stripes" },
  { value: "patch", label: "Eye patch" },
  { value: "socks", label: "Brows" },
];
export const EYES = [
  { value: "round", label: "Round" },
  { value: "happy", label: "Happy" },
  { value: "sleepy", label: "Sleepy" },
  { value: "wink", label: "Wink" },
];
export const HATS = [
  { value: "none", label: "None" },
  { value: "beanie", label: "Beanie" },
  { value: "crown", label: "Crown" },
  { value: "bow", label: "Bow" },
  { value: "party", label: "Party hat" },
  { value: "flower", label: "Flower" },
];
export const SEASON_HATS = { witch: "Witch hat", pumpkin: "Pumpkin", bat: "Bat buddy", santa: "Santa hat" };

function hash(id) {
  let h = 7;
  for (let i = 0; i < (id || "").length; i++) h = (h * 33 + id.charCodeAt(i)) >>> 0;
  return h;
}

export function catLook(id) {
  const h = hash(id);
  return {
    fur: FURS[h % FURS.length],
    pattern: PATTERN_KEYS[(h >>> 4) % PATTERN_KEYS.length],
    eyes: ((h >>> 8) % 3) === 0 ? "happy" : "round",
    hat: "none",
  };
}

function Eyes({ kind, color }) {
  const round = (cx) => (
    <>
      <ellipse cx={cx} cy="36" rx="3.2" ry="3.9" fill={color} />
      <circle cx={cx + 1.2} cy="34.6" r="1.1" fill="#fff" />
    </>
  );
  const arc = (x, down) => <path d={`M${x} 37 q4 ${down ? 3 : -5} 8 0`} stroke={color} strokeWidth="2.6" fill="none" strokeLinecap="round" />;
  if (kind === "happy") return <>{arc(19)}{arc(37)}</>;
  if (kind === "sleepy") return <>{arc(19, true)}{arc(37, true)}</>;
  if (kind === "wink") return <>{round(23)}{arc(37)}</>;
  if (kind === "hearts") {
    const heart = (cx) => <path d={`M${cx} 40 l-4.4 -4.4 a2.6 2.6 0 0 1 4.4 -3.2 a2.6 2.6 0 0 1 4.4 3.2 Z`} fill="#EC4899" />;
    return <>{heart(23)}{heart(41)}</>;
  }
  if (kind === "gasp") {
    const big = (cx) => (
      <>
        <ellipse cx={cx} cy="35.5" rx="4.4" ry="5.2" fill={color} />
        <circle cx={cx + 1.5} cy="33.6" r="1.6" fill="#fff" />
      </>
    );
    return <>{big(23)}{big(41)}</>;
  }
  if (kind === "angry") {
    return (
      <>
        <path d="M18 32.5 L27 35.5 M46 32.5 L37 35.5" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="23.5" cy="38.4" r="2.1" fill={color} />
        <circle cx="40.5" cy="38.4" r="2.1" fill={color} />
      </>
    );
  }
  return <>{round(23)}{round(41)}</>;
}

export const MOODS = [
  { value: "love", label: "Purr" },
  { value: "laugh", label: "Haha" },
  { value: "gasp", label: "Gasp" },
  { value: "hiss", label: "Hiss" },
  { value: "paw", label: "High paw" },
  { value: "sleepy", label: "Zzz" },
];
const MOOD_EYES = { love: "hearts", laugh: "happy", gasp: "gasp", hiss: "angry", paw: "wink", sleepy: "sleepy" };

function Mouth({ mood, line }) {
  if (mood === "laugh") return <path d="M26 45.5 Q32 55 38 45.5 Z" fill="#7A2E2E" stroke={line} strokeWidth="1.4" strokeLinejoin="round" />;
  if (mood === "gasp") return <ellipse cx="32" cy="49" rx="2.6" ry="3.3" fill="#7A2E2E" />;
  if (mood === "hiss") {
    return (
      <g>
        <path d="M25 46 Q32 53 39 46 Z" fill="#7A2E2E" stroke={line} strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M27.5 46.2 l1.2 3 l1.2 -3 Z M34.1 46.2 l1.2 3 l1.2 -3 Z" fill="#fff" />
      </g>
    );
  }
  if (mood === "sleepy") return <path d="M29.5 47.5 q2.5 1.5 5 0" stroke={line} strokeWidth="1.5" fill="none" strokeLinecap="round" />;
  return <path d="M32 45.5 q-2 3 -5 2 M32 45.5 q2 3 5 2" stroke={line} strokeWidth="1.6" fill="none" strokeLinecap="round" />;
}

function RaisedPaw({ fur, line }) {
  return (
    <g transform="translate(52 50) rotate(-15)">
      <ellipse rx="7.5" ry="6.5" fill={fur} stroke={line} strokeWidth="2" />
      <ellipse cy="1.5" rx="3" ry="2.4" fill="#F4B6A6" />
      <circle cx="-3.6" cy="-2.6" r="1.3" fill="#F4B6A6" />
      <circle cx="0" cy="-3.8" r="1.3" fill="#F4B6A6" />
      <circle cx="3.6" cy="-2.6" r="1.3" fill="#F4B6A6" />
    </g>
  );
}

function Hat({ kind, line }) {
  const s = { stroke: line, strokeWidth: 2, strokeLinejoin: "round" };
  switch (kind) {
    case "beanie":
      return (
        <g>
          <path d="M10 27 Q10 1 32 1 Q54 1 54 27 Z" fill="#C85A32" {...s} />
          <path d="M18 8 Q20 18 18 24 M26 4 Q27 15 26 24 M38 4 Q37 15 38 24 M46 8 Q44 18 46 24" stroke="rgba(255,255,255,0.35)" strokeWidth="2" fill="none" />
          <rect x="8" y="21" width="48" height="8" rx="4" fill="#9E4422" {...s} />
          <circle cx="32" cy="-2" r="6" fill="#F2D7B0" {...s} />
        </g>
      );
    case "crown":
      return (
        <g>
          <path d="M19 18 L20 3 L26 10 L32 0 L38 10 L44 3 L45 18 Z" fill="#F2C14E" {...s} />
          <circle cx="32" cy="12" r="2.2" fill="#DC2626" />
          <circle cx="25" cy="14" r="1.6" fill="#2563EB" />
          <circle cx="39" cy="14" r="1.6" fill="#2563EB" />
        </g>
      );
    case "bow":
      return (
        <g transform="translate(45 14) rotate(15)">
          <path d="M0 0 L-10 -7 L-10 7 Z" fill="#DB2777" {...s} strokeWidth="1.8" />
          <path d="M0 0 L10 -7 L10 7 Z" fill="#DB2777" {...s} strokeWidth="1.8" />
          <circle r="3" fill="#F472B6" stroke={line} strokeWidth="1.5" />
        </g>
      );
    case "party":
      return (
        <g>
          <path d="M23 19 L32 -9 L41 19 Z" fill="#7C3AED" {...s} />
          <path d="M26.5 9 L37.5 9 M29 1 L35 1" stroke="#F2C14E" strokeWidth="3" />
          <circle cx="32" cy="-10" r="3.5" fill="#F2C14E" stroke={line} strokeWidth="1.5" />
        </g>
      );
    case "flower":
      return (
        <g transform="translate(18 16)">
          {[0, 72, 144, 216, 288].map((a) => (
            <ellipse key={a} cx="0" cy="-5" rx="3.2" ry="5" fill="#FDE68A" stroke={line} strokeWidth="1.2" transform={`rotate(${a})`} />
          ))}
          <circle r="3" fill="#D97706" />
        </g>
      );
    case "witch":
      return (
        <g>
          <ellipse cx="32" cy="17" rx="25" ry="5" fill="#2E1F3B" {...s} />
          <path d="M19 16 Q24 -2 30 -10 Q36 -14 44 -8 Q38 -6 37 0 Q40 8 45 16 Z" fill="#2E1F3B" {...s} />
          <path d="M20.5 11 Q32 7.5 43.5 11 L44.5 15 Q32 11.5 19.5 15 Z" fill="#E8894A" />
          <rect x="29.5" y="9.5" width="5" height="4.5" fill="none" stroke="#F2C14E" strokeWidth="1.5" />
        </g>
      );
    case "pumpkin":
      return (
        <g>
          <path d="M32 0 Q31 -5 35 -7" stroke="#4D7C5F" strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M34 -3 Q39 -6 40 -2 Q37 -1 34 -3 Z" fill="#65A30D" />
          <ellipse cx="32" cy="9" rx="13" ry="9.5" fill="#F28C28" {...s} />
          <ellipse cx="32" cy="9" rx="5.5" ry="9.5" fill="none" stroke="rgba(0,0,0,0.22)" strokeWidth="1.6" />
          <path d="M25.5 7 l2.5 -3 l2.5 3 Z M33.5 7 l2.5 -3 l2.5 3 Z" fill="#7A3B0A" />
          <path d="M26 11.5 q6 4 12 0 q-6 1.8 -12 0" fill="#7A3B0A" />
        </g>
      );
    case "bat":
      return (
        <g transform="translate(32 9)">
          <path d="M0 0 Q-6 -8 -16 -6 Q-12 -3 -13 1 Q-9 -1 -7 3 Q-4 0 0 3 Q4 0 7 3 Q9 -1 13 1 Q12 -3 16 -6 Q6 -8 0 0 Z" fill="#2C221E" stroke="#5A5054" strokeWidth="1" strokeLinejoin="round" />
          <circle r="4" fill="#2C221E" />
          <path d="M-3 -3 L-1.8 -7 L-0.6 -3.6 Z M3 -3 L1.8 -7 L0.6 -3.6 Z" fill="#2C221E" />
          <circle cx="-1.4" cy="-0.6" r="0.9" fill="#F2C14E" />
          <circle cx="1.4" cy="-0.6" r="0.9" fill="#F2C14E" />
        </g>
      );
    case "santa":
      return (
        <g>
          <path d="M12 21 Q18 -2 36 -6 Q50 -6 56 8 Q48 2 44 8 Q50 14 52 21 Z" fill="#DC2626" {...s} />
          <rect x="9" y="17" width="46" height="8" rx="4" fill="#fff" {...s} />
          <circle cx="56" cy="9" r="5" fill="#fff" {...s} />
        </g>
      );
    default:
      return null;
  }
}

export function CatFace({ id, look, mood, size = 36, withYarn = false, className = "", testid }) {
  const { fur, pattern, eyes, hat } = look || catLook(id);
  const dark = fur === "#3B3335";
  const line = dark ? "#1A1416" : "rgba(44,34,30,0.85)";
  const eye = dark ? "#E8D44D" : "#2C221E";
  const mark = dark ? "#5A5054" : "rgba(44,34,30,0.35)";
  return (
    <span className={`relative inline-block shrink-0 ${className}`} style={{ width: size, height: size }} data-testid={testid}>
      <svg viewBox="-4 -14 72 78" width={size} height={size} aria-hidden="true">
        <path d="M11 30 L13 5 L30 18 Z" fill={fur} stroke={line} strokeWidth="2.2" strokeLinejoin="round" />
        <path d="M53 30 L51 5 L34 18 Z" fill={fur} stroke={line} strokeWidth="2.2" strokeLinejoin="round" />
        <path d="M16 22 L17 12 L25 18 Z" fill="#F4B6A6" />
        <path d="M48 22 L47 12 L39 18 Z" fill="#F4B6A6" />
        <ellipse cx="32" cy="37" rx="23" ry="20" fill={fur} stroke={line} strokeWidth="2.2" />
        {pattern === "stripes" && <path d="M32 18 v7 M25 19.5 l1.5 5.5 M39 19.5 l-1.5 5.5" stroke={mark} strokeWidth="2.6" strokeLinecap="round" />}
        {pattern === "patch" && <ellipse cx="22" cy="35" rx="8.5" ry="7.5" fill={dark ? "#F2D7B0" : "#FFF7EE"} opacity="0.9" />}
        {pattern === "socks" && <path d="M14 30 q4 -6 9 -3 M50 30 q-4 -6 -9 -3" stroke={mark} strokeWidth="2.4" fill="none" strokeLinecap="round" />}
        <ellipse cx="32" cy="46" rx="9.5" ry="6.5" fill={dark ? "#5A5054" : "#FFF7EE"} opacity="0.85" />
        <Eyes kind={mood ? MOOD_EYES[mood] : eyes} color={eye} />
        <path d="M29.3 42.5 h5.4 l-2.7 3.2 z" fill="#E77F86" />
        <Mouth mood={mood} line={line} />
        <path d="M6 42 h11 M7 47.5 l10 -2.5 M58 42 h-11 M57 47.5 l-10 -2.5" stroke={dark ? "#C9C2C4" : line} strokeWidth="1.3" opacity="0.6" strokeLinecap="round" />
        <Hat kind={hat} line={line} />
        {mood === "paw" && <RaisedPaw fur={fur} line={line} />}
      </svg>
      {withYarn && (
        <span className="absolute -bottom-1 -right-1">
          <YarnBall size={Math.max(12, size * 0.42)} color={yarnColor(id)} />
        </span>
      )}
    </span>
  );
}
